/** Order id when the customer pressed Retry — ignore pre-reprice GetOrder snapshots. */
export interface CommitRetryRepriceSession {
  orderIdAtPress: string | null;
}

export type CommitRetryRepriceDecision =
  | { kind: 'idle' }
  | { kind: 'wait' }
  | { kind: 'run_place_order'; rbiOrderId: string }
  | { kind: 'reprice_failed' };

export interface CommitRetryRepriceInput {
  pendingCommitRetry: boolean;
  session: CommitRetryRepriceSession | null;
  repriceMutationSettled: boolean;
  priceMutationLoading: boolean;
  serverOrderStatus?: string;
  serverOrderRbiOrderId?: string | null;
  priceOrderRbiOrderId?: string | null;
  hasPriceMutationError: boolean;
  serverOrderError: boolean;
}

export function createCommitRetryRepriceSession(
  orderIdAtPress: string | null | undefined
): CommitRetryRepriceSession {
  return { orderIdAtPress: orderIdAtPress ?? null };
}

/** Inputs for knowing the PriceOrder mutation for this retry press has finished. */
export interface CommitRetryRepriceMutationSettleInput {
  orderIdAtPress: string | null;
  repriceRequested: boolean;
  mutationObservedInFlight: boolean;
  mutationLoading: boolean;
  prevMutationLoading: boolean;
  sawPriceRequested: boolean;
  priceOrderRbiOrderId?: string | null;
  serverOrderStatus?: string;
  serverOrderRbiOrderId?: string | null;
}

/**
 * The PriceOrder mutation can finish before React ever renders mutationLoading=true.
 * Treat GetOrder/Apollo id changes (after the press) as settlement signals too.
 */
export function shouldMarkCommitRetryRepriceMutationSettled(
  input: CommitRetryRepriceMutationSettleInput
): boolean {
  if (input.mutationLoading) {
    return false;
  }

  const engaged =
    input.repriceRequested || input.mutationObservedInFlight || input.sawPriceRequested;

  if (!engaged) {
    return false;
  }

  const mutationJustFinished =
    input.mutationObservedInFlight && input.prevMutationLoading && !input.mutationLoading;

  if (mutationJustFinished) {
    return true;
  }

  const orderIdAtPress = input.orderIdAtPress;
  const mutationId = input.priceOrderRbiOrderId ?? null;
  const polledId = input.serverOrderRbiOrderId ?? null;

  if (orderIdAtPress && mutationId && mutationId !== orderIdAtPress) {
    return true;
  }

  if (
    orderIdAtPress &&
    input.serverOrderStatus === 'PRICE_SUCCESSFUL' &&
    polledId &&
    polledId !== orderIdAtPress
  ) {
    return true;
  }

  // In-place reprice: same id, but GetOrder went through PRICE_REQUESTED for this attempt.
  if (
    orderIdAtPress &&
    input.sawPriceRequested &&
    input.serverOrderStatus === 'PRICE_SUCCESSFUL' &&
    polledId &&
    polledId === orderIdAtPress
  ) {
    return true;
  }

  return false;
}

function resolvePricedOrderId(input: CommitRetryRepriceInput): string | null {
  const polledId = input.serverOrderRbiOrderId ?? null;
  const mutationId = input.priceOrderRbiOrderId ?? null;
  const orderIdAtPress = input.session?.orderIdAtPress ?? null;

  if (!polledId || input.serverOrderStatus !== 'PRICE_SUCCESSFUL') {
    return null;
  }

  if (mutationId && polledId === mutationId) {
    return polledId;
  }

  // GetOrder often updates before Apollo exposes the new mutation rbiOrderId.
  if (orderIdAtPress && polledId !== orderIdAtPress) {
    return polledId;
  }

  return null;
}

function isRepriceFailure(input: CommitRetryRepriceInput): boolean {
  if (input.hasPriceMutationError) {
    return true;
  }

  if (!input.repriceMutationSettled) {
    return false;
  }

  const orderIdAtPress = input.session?.orderIdAtPress ?? null;
  const polledId = input.serverOrderRbiOrderId ?? null;

  if (input.serverOrderStatus === 'PRICE_ERROR' || input.serverOrderError) {
    return true;
  }

  if (input.serverOrderStatus === 'VALIDATION_ERROR') {
    return true;
  }

  if (
    input.serverOrderStatus === 'PAYMENT_ERROR' &&
    polledId &&
    orderIdAtPress &&
    polledId !== orderIdAtPress
  ) {
    return true;
  }

  return false;
}

/**
 * Commit retry: reprice via tryPricing(), then run the payment pipeline once GetOrder
 * reports PRICE_SUCCESSFUL for the priced order.
 */
export function getCommitRetryRepriceDecision(
  input: CommitRetryRepriceInput
): CommitRetryRepriceDecision {
  if (!input.pendingCommitRetry || !input.session) {
    return { kind: 'idle' };
  }

  if (!input.repriceMutationSettled || input.priceMutationLoading) {
    return { kind: 'wait' };
  }

  if (isRepriceFailure(input)) {
    return { kind: 'reprice_failed' };
  }

  const rbiOrderId = resolvePricedOrderId(input);
  if (!rbiOrderId) {
    return { kind: 'wait' };
  }

  return { kind: 'run_place_order', rbiOrderId };
}
